import { getActiveInteraction } from '@atlaskit/react-ufo/interaction-metrics';
import InteractionContext, { type InteractionContextType } from '@atlaskit/interaction-context';
import { UFOInteractionContextType } from '@atlaskit/react-ufo/interaction-context';
import * as Sentry from '@sentry/react';

import React, { type Context } from 'react';
import { UFOSegment } from '../analytics/react-ufo';
import { ExperienceSLOKey } from './config';
import { reportUFOError, toError } from '../analytics/react-ufo/useReportUFOError';

interface UFOErrorReporterProps {
	children: React.ReactNode;
	ufo: ExperienceSLOKey;
}

interface UFOErrorReporterState {
	childHasThrown: boolean;
}

/**
 * This "soft" error boundary reports UFO failure events only for the outermost, originating SLO component.
 */
class UFOErrorReporter extends React.Component<UFOErrorReporterProps, UFOErrorReporterState> {
	static contextType: Context<InteractionContextType | null> = InteractionContext;
	context!: React.ContextType<Context<UFOInteractionContextType | null>>;

	constructor(props: UFOErrorReporterProps) {
		super(props);
		this.state = { childHasThrown: false };
	}

	static getDerivedStateFromError(_: Error): UFOErrorReporterState {
		return { childHasThrown: true };
	}

	override render(): React.ReactNode {
		return <>{this.state.childHasThrown ? null : this.props.children}</>;
	}

	override componentDidCatch(maybeError: unknown, _: React.ErrorInfo): never {
		// original signature: componentDidCatch?(error: Error, errorInfo: ErrorInfo): void
		// …but React isn't telling the truth: error may not be an Error… hence the below verification
		const error: Error = toError(maybeError);

		if (reportUFOError(error, this.props.ufo, this.context)) {
			Sentry.withScope((scope) => {
				scope.setTag('slo-key', this.props.ufo);
				scope.setTag('interaction-id', getActiveInteraction()?.id);
				scope.captureException(error, { mechanism: { handled: false } });
			});
		}

		throw error;
	}
}

/**
 * Use this component to monitor the reliability and performance of its children via an auto-generated service-level
 * objective.
 * @param children
 * @param key The React UFO experience key for which to maintain an SLO.
 */
export const ExperienceSLO = ({
	children,
	ufo,
}: {
	children: React.ReactNode;
	ufo: ExperienceSLOKey;
}): React.JSX.Element => {
	return (
		<UFOSegment name={ufo}>
			<UFOErrorReporter ufo={ufo}>{children}</UFOErrorReporter>
		</UFOSegment>
	);
};
